import { useState } from 'react'
import classNames from 'classnames'
import { find, isEqual } from 'lodash'

import { InfoButton } from 'components/buttons'
import classes from './SelectableOptions.styles.scss'

export type OptionType = string | { group: string; size: string }

export interface SelectableOptionsProps {
  options: OptionType[]
  maxSelected?: number
  onSelect: (selectedOptions: OptionType[]) => void
  selectedOptions?: OptionType[]
}

export const SelectableOptions = ({
  options,
  maxSelected = 1,
  onSelect,
  selectedOptions: initialSelectedOptions = [],
}: SelectableOptionsProps) => {
  const [selectedOptions, setSelectedOptions] = useState<OptionType[]>(
    initialSelectedOptions
  )
  const handleSelectOption = (option: OptionType) => {
    let newSelectedOptions = [...selectedOptions]
    let previousSelection = find(selectedOptions, (selected) =>
      isEqual(selected, option)
    )
    // If the option is already selected, remove it.
    if (previousSelection) {
      newSelectedOptions = selectedOptions.filter(
        (selectedOption) => selectedOption !== previousSelection
      )
    }
    // If the maximum number of selections hasn't been reached, add the option.
    else if (selectedOptions.length < maxSelected) {
      newSelectedOptions = [...selectedOptions, option]
    }
    // If the maximum number of selections is 1 and a new option is selected, replace the previous selection.
    else if (maxSelected === 1) {
      newSelectedOptions = [option]
    }

    setSelectedOptions(newSelectedOptions)
    onSelect(newSelectedOptions)
  }

  // Group options by group name.
  const groupedOptions: { group: string; sizes: OptionType[]; id: string }[] =
    []
  options.forEach((option, index) => {
    let group: { group: string; sizes: OptionType[]; id: string } | undefined

    if (typeof option === 'string') {
      group = groupedOptions.find((g) => g.group === '')
    } else {
      group = groupedOptions.find((g) => g.group === option.group)
    }

    if (group) {
      group.sizes.push(option)
    } else {
      groupedOptions.push({
        group: typeof option === 'string' ? '' : option.group,
        sizes: [option],
        id: `${typeof option === 'string' ? '' : option.group}-${index}`,
      })
    }
  })

  return (
    <div>
      {groupedOptions.map((group) => (
        <div key={group.id}>
          {group.group && (
            <h4 className={classes.groupHeading}>{group.group}</h4>
          )}
          <div>
            {group.sizes.map((size, index) => {
              const isSelected = selectedOptions.some((selected) => {
                if (typeof selected === 'string' && typeof size === 'string') {
                  return selected === size
                }

                return (
                  typeof selected !== 'string' &&
                  typeof size !== 'string' &&
                  selected.group === size.group &&
                  selected.size === size.size
                )
              })
              return (
                <InfoButton
                  pill
                  className={classNames(classes.button)}
                  empty={!isSelected}
                  key={`${group.id}-${index}`}
                  size="small"
                  onClick={() => handleSelectOption(size)}
                >
                  {typeof size === 'string' ? size : size.size}
                </InfoButton>
              )
            })}
          </div>
        </div>
      ))}
      {maxSelected > 1 && (
        <p className={classes.helpText}>Max: {maxSelected}.</p>
      )}
    </div>
  )
}
